// Admin.js — Pinterest-like Masonry (STYLE ONLY)
// ✅ Goals (per request):
//  - Keep the masonry layout
//  - Use FULL PAGE WIDTH (remove left padding reserved for sidebar)
//  - Show media in ORIGINAL proportions (NO ZOOM / NO CROP)
//  - Keep pins balanced: very tall portrait VIDEOS get ~half-height tiles (still full video visible)
//  - Never show white frames
// ⚠️ Business logic preserved: endpoints, paging, caching, filters, finals, users avatars

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HeaderRunwayStyle from "./HeaderRunwayStyle";
import FooterRunwayStyle from "./FooterRunwayStyle";
import { Link } from "react-router-dom";
const PAGE_SIZE = 40;

// Cache TTL (client-side)
const CACHE_TTL_MS = 3 * 60 * 1000;

// Module-scope caches (persist across mounts)
const ADMIN_AGG_CACHE = new Map(); // key -> { items, offset, hasMore, ts }
const ADMIN_PAGE_CACHE = new Map(); // key -> { page, ts }

// ✅ Memorize measured aspect ratios to avoid layout “decallage” when coming back / scrolling
const MEDIA_AR_CACHE = new Map(); // src -> aspectRatio (width/height)

/**
 * Lazy media loader: loads near viewport.
 * ✅ Keeps FULL media visible (no crop) with the ORIGINAL aspect ratio (no zoom).
 * ✅ For portrait VIDEOS: reduce preview tile height ~50% (still full video visible via object-fit: contain)
 */
function LazyMediaVisual({ isImage, src, title, isVideo }) {
  const holderRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // aspectRatio = width / height
  const [aspectRatio, setAspectRatio] = useState(() => {
    const cached = MEDIA_AR_CACHE.get(src);
    return typeof cached === "number" && Number.isFinite(cached) && cached > 0 ? cached : 4 / 3;
  });

  const setARFromWH = useCallback(
    (w, h) => {
      const W = Number(w);
      const H = Number(h);
      if (!Number.isFinite(W) || !Number.isFinite(H) || W <= 0 || H <= 0) return;

      const r = W / H;

      // Keep original ratio (NO CLAMP). Just ignore corrupted extremes.
      if (!Number.isFinite(r) || r <= 0.05 || r >= 50) return;

      MEDIA_AR_CACHE.set(src, r);
      setAspectRatio(r);
    },
    [src]
  );

  // ✅ Display aspect ratio (tile) — for portrait VIDEOS only, halve the height by doubling w/h ratio.
  const displayAspectRatio = useMemo(() => {
    const r = aspectRatio;
    if (!Number.isFinite(r) || r <= 0) return r;

    // Only apply to videos, and only if portrait (height > width)
    if (isVideo && r < 1) {
      return r * 2;
    }

    return r;
  }, [aspectRatio, isVideo]);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && e.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "700px 0px" }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={holderRef} className={`wv-pin-thumb-wrap ${isVideo ? "wv-pin-is-video" : ""}`} style={{ aspectRatio: displayAspectRatio }}>
      {!shouldLoad ? (
        <div className="wv-pin-skeleton" />
      ) : isImage ? (
        <img
          src={src}
          alt={title || "media"}
          className="wv-pin-thumb"
          loading="lazy"
          decoding="async"
          style={{ objectFit: "contain", objectPosition: "center" }}
          onLoad={(e) => {
            const img = e.currentTarget;
            setARFromWH(img?.naturalWidth, img?.naturalHeight);
          }}
        />
      ) : (
        <video
          src={src}
          preload="metadata"
          className="wv-pin-thumb"
          muted
          playsInline
          style={{ objectFit: "contain", objectPosition: "center" }}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            setARFromWH(v?.videoWidth, v?.videoHeight);

            // Force an early frame (helps previews for some browsers)
            try {
              if (typeof v?.currentTime === "number") v.currentTime = Math.min(0.1, v.duration || 0.1);
            } catch {
              // ignore
            }
          }}
        />
      )}
    </div>
  );
}

export default function Decouvrir() {
  // Main list is paged
  const [mediaList, setMediaList] = useState([]);

  const [filter, setFilter] = useState("all");
  const [contributorFilter, setContributorFilter] = useState("");
  const [finals, setFinals] = useState([]);

  // Paging state
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef = useRef(0);
  const loadingRef = useRef(false);

  // ✅ Topbar dynamic spacer to avoid cut/overlap on any screen width/zoom
  const topbarRef = useRef(null);
  const [topbarH, setTopbarH] = useState(76);

  useEffect(() => {
    const el = topbarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const update = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (!h) return;
      setTopbarH((prev) => (prev === h ? prev : h));
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 👤 Users / avatars
  const [usersByUsername, setUsersByUsername] = useState({});
  const [avatarBust, setAvatarBust] = useState(0);

  // Refresh avatars when profile is updated (same event used in Navbar.js)
  useEffect(() => {
    const onProfileUpdated = () => setAvatarBust(Date.now());
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  const appendBust = (url) => {
    if (!url) return url;
    if (!avatarBust) return url;
    return `${url}${url.includes("?") ? "&" : "?"}v=${avatarBust}`;
  };

  const normalizeUploadPath = (p) => {
    let s = String(p || "").trim();
    if (!s) return "";
    if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/uploads/")) return s;
    while (s.startsWith("/")) s = s.slice(1);
    return "/uploads/" + s;
  };

  const avatarFor = (username, labelForFallback = "") => {
    const u = usersByUsername[username];

    // ✅ Prefer avatar_url like Navbar.js
    const avatarUrl = u?.avatar_url || u?.avatarUrl || u?.avatar || u?.photo_url;
    if (avatarUrl) return appendBust(String(avatarUrl));

    // ✅ Fallback to stored profilepath
    const profilePath = u?.profilepath || u?.profilePath || u?.profile_path;
    if (profilePath) return appendBust(normalizeUploadPath(profilePath));

    // ✅ Last fallback (better than default png)
    const fallbackName = labelForFallback || username || "User";
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(fallbackName)}&background=dee2e6&color=495057`;
  };

  const getMediaType = (fileName) => {
    const ext = (fileName || "").split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    if (["mp4", "webm", "ogg", "mov", "mkv"].includes(ext)) return "video";
    return "file";
  };


  const isPublicItem = (item) => {
    const v = (item?.visibility || "").toString().toUpperCase().trim();
    if (!v) return true; // default public when field is missing
    return v === "PUBLIC";
  };


  const getTypeParamFromFilter = (f) => {
    if (f === "videos") return "video";
    if (f === "images") return "image";
    return null;
  };

  // ✅ Sorting helpers
  const getCreatedTs = (it) => {
    const raw = it?.created_at ?? it?.createdAt ?? it?.date ?? it?.eventdate;
    if (raw == null) return 0;

    if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;

    const s = String(raw).trim();
    if (!s) return 0;

    // numeric timestamps (ms)
    if (/^[0-9]{11,}$/.test(s)) {
      const n = Number(s);
      return Number.isFinite(n) ? n : 0;
    }

    const d = new Date(s);
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const formatDateFr = (raw) => {
    if (raw == null) return "";
    const d = new Date(raw);
    const t = d.getTime();
    if (!Number.isFinite(t)) return "";
    try {
      return d.toLocaleDateString("fr-FR");
    } catch {
      return "";
    }
  };

  // ✅ Build a stable cache key per server query
  const buildBaseKey = useCallback(() => {
    const typeParam = getTypeParamFromFilter(filter) || "all";
    return `type=${typeParam}|limit=${PAGE_SIZE}|light=1`;
  }, [filter]);

  const isFresh = (ts) => Date.now() - ts < CACHE_TTL_MS;

  const loadPage = useCallback(
    async ({ reset = false } = {}) => {
      // No need to fetch media pages when filter is "closed"
      if (filter === "closed") {
        setMediaList([]);
        setHasMore(false);
        return;
      }

      const baseKey = buildBaseKey();

      // ✅ Serve aggregate cache instantly on reset
      if (reset) {
        const cachedAgg = ADMIN_AGG_CACHE.get(baseKey);
        if (cachedAgg && isFresh(cachedAgg.ts)) {
          setMediaList(cachedAgg.items || []);
          offsetRef.current = cachedAgg.rawOffset ?? cachedAgg.offset ?? (cachedAgg.items || []).length;

          setHasMore(!!cachedAgg.hasMore);
          return;
        }
      }

      if (loadingRef.current) return;
      loadingRef.current = true;
      setIsLoading(true);

      try {
        const typeParam = getTypeParamFromFilter(filter);

        const offset = reset ? 0 : offsetRef.current;
        const pageKey = `${baseKey}|offset=${offset}`;

        // ✅ Serve page cache for "load more"
        const cachedPage = ADMIN_PAGE_CACHE.get(pageKey);
        if (cachedPage && isFresh(cachedPage.ts)) {
          const arr = cachedPage.page || [];
          const rawLen = cachedPage.rawLen ?? arr.length;

          if (reset) {
            setMediaList(arr);
            offsetRef.current = rawLen;
          } else {
            setMediaList((prev) => [...prev, ...arr]);
            offsetRef.current = offsetRef.current + rawLen;
          }

          const nextHasMore = rawLen === PAGE_SIZE;
          setHasMore(nextHasMore);

          // ✅ Update aggregate cache
          const newItems = reset ? arr : [...(ADMIN_AGG_CACHE.get(baseKey)?.items || []), ...arr];

          ADMIN_AGG_CACHE.set(baseKey, {
            items: newItems,
            offset: offsetRef.current,
            rawOffset: offsetRef.current,
            hasMore: nextHasMore,
            ts: Date.now(),
          });

          return;
        }

        const params = new URLSearchParams();
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(offset));
        params.set("light", "1");
        if (typeParam) params.set("type", typeParam);

        const res = await fetch(`/media?${params.toString()}`);
        const data = res.ok ? await res.json() : [];
        const rawArr = Array.isArray(data) ? data : [];

        // ✅ Public only
        const arr = rawArr.filter(isPublicItem);

        // ✅ Store page cache (public page + rawLen for paging)
        ADMIN_PAGE_CACHE.set(pageKey, { page: arr, rawLen: rawArr.length, ts: Date.now() });

        if (reset) {
          setMediaList(arr);
          offsetRef.current = rawArr.length;
        } else {
          setMediaList((prev) => [...prev, ...arr]);
          offsetRef.current = offsetRef.current + rawArr.length;
        }

        const nextHasMore = rawArr.length === PAGE_SIZE;
        setHasMore(nextHasMore);

        // ✅ Update aggregate cache
        const prevAgg = ADMIN_AGG_CACHE.get(baseKey);
        const merged = reset ? arr : [...(prevAgg?.items || []), ...arr];

        ADMIN_AGG_CACHE.set(baseKey, {
          items: merged,
          offset: offsetRef.current,
          rawOffset: offsetRef.current,
          hasMore: nextHasMore,
          ts: Date.now(),
        });

      } catch (e) {
        if (reset) setMediaList([]);
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setIsLoading(false);
      }
    },
    [filter, buildBaseKey]
  );

  const resetAndLoad = useCallback(() => {
    offsetRef.current = 0;
    setHasMore(true);
    setMediaList([]);
    loadPage({ reset: true });
  }, [loadPage]);

  // 🔄 Load medias (paged) when filter changes
  useEffect(() => {
    resetAndLoad();
  }, [filter, resetAndLoad]);

  // 🔄 Load projects → finals for closed ones
  useEffect(() => {
    fetch("/projects")
      .then((r) => (r.ok ? r.json() : []))
      .then((projects = []) => {
        const closed = (projects || []).filter((p) => {
          const s = (p.status || "").toLowerCase();
          return s.includes("clôturé") || s.includes("term");
        });

        const mapped = closed.map((p) => ({
          __kind: "project-final",
          id: `final-${p.id}`,
          projectId: p.id,
          title: p.title || "Projet",
          created_at: p.created_at,
          user: p.created_by || "",
          final_src: `/output/project_${p.id}_final.mp4`,
          visibility: p.visibility || "",
        }));

        setFinals(mapped.filter(isPublicItem));
      })
      .catch(() => setFinals([]));
  }, []);

  // (Public page) Users endpoint is protected; using fallback avatars.

  // ✅ Fake contributors group (same idea as Landing): stable avatars + stable group size per item
  const fakeUsers = useMemo(() => {
    const NAMES = [
      "Maya", "Sami", "Lina", "Noah", "Inès", "Yassine", "Emma", "Rami",
      "Chloé", "Adam", "Nora", "Karim", "Sarah", "Mehdi", "Aya", "Younes",
      "Lea", "Omar", "Nadine", "Walid"
    ];

    const AVATAR_POOL = [
      "https://randomuser.me/api/portraits/women/12.jpg",
      "https://randomuser.me/api/portraits/men/32.jpg",
      "https://randomuser.me/api/portraits/women/45.jpg",
      "https://randomuser.me/api/portraits/men/54.jpg",
      "https://randomuser.me/api/portraits/women/68.jpg",
      "https://randomuser.me/api/portraits/men/15.jpg",
      "https://randomuser.me/api/portraits/women/33.jpg",
      "https://randomuser.me/api/portraits/men/71.jpg",
      "https://randomuser.me/api/portraits/women/26.jpg",
      "https://randomuser.me/api/portraits/men/9.jpg",
      "https://randomuser.me/api/portraits/women/6.jpg",
      "https://randomuser.me/api/portraits/men/22.jpg",
      "https://randomuser.me/api/portraits/women/52.jpg",
      "https://randomuser.me/api/portraits/men/60.jpg",
      "https://randomuser.me/api/portraits/women/75.jpg",
      "https://randomuser.me/api/portraits/men/81.jpg",
      "https://randomuser.me/api/portraits/women/19.jpg",
      "https://randomuser.me/api/portraits/men/44.jpg",
    ];

    const hash32 = (v) => {
      const s = String(v ?? "");
      let h = 2166136261;
      for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    };

    const COUNT = 18;
    const baseSeed = 987654321;
    return Array.from({ length: COUNT }, (_, i) => {
      const name = NAMES[i % NAMES.length];
      const seed = hash32(`${baseSeed}-${i}-${name}`);
      const img = AVATAR_POOL[seed % AVATAR_POOL.length];
      return { name: `${name} ${String.fromCharCode(65 + (i % 26))}.`, img };
    });
  }, []);

  const pickUsers = useCallback((seedVal) => {
    const h = (typeof seedVal === "string" || typeof seedVal === "number") ? (seedVal + "") : JSON.stringify(seedVal ?? "");
    let seed = 0;
    for (let i = 0; i < h.length; i++) seed = (seed * 31 + h.charCodeAt(i)) >>> 0;

    const used = new Set();
    const out = [];
    for (let k = 0; k < 3; k++) {
      let idx = (seed + k * 7) % fakeUsers.length;
      while (used.has(idx)) idx = (idx + 1) % fakeUsers.length;
      used.add(idx);
      out.push(fakeUsers[idx]);
    }
    return out;
  }, [fakeUsers]);

  const pickUserCount = useCallback((seedVal) => {
    const h = (typeof seedVal === "string" || typeof seedVal === "number") ? (seedVal + "") : JSON.stringify(seedVal ?? "");
    let seed = 0;
    for (let i = 0; i < h.length; i++) seed = (seed * 31 + h.charCodeAt(i)) >>> 0;

    const min = 6;
    const max = 42;
    return min + (seed % (max - min + 1));
  }, []);



  const resolveProjectCreatorUsername = (m) => m?.project_created_by || m?.project_creator || m?.created_by || m?.owner || m?.user || "";

  const resolveProjectCreatorLabel = (m) => {
    const u = String(resolveProjectCreatorUsername(m) || "").trim();
    if (!u) return "—";
    return u.includes("@") ? u.split("@")[0] : u;
  };

  const resolveUserProfilePath = (username) => {
    const u = String(username || "").trim();
    if (!u) return null;
    return `/users/${encodeURIComponent(u)}`;
  };

  const toCamelCaseName = (s) => {
    const raw = String(s || "").trim();
    if (!raw) return "";
    return raw
      .split(/[^a-zA-Z0-9]+/g)
      .filter(Boolean)
      .map((w) => {
        const lower = String(w).toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(" ");
  };

  const getCounters = (item) => {
    const likes = Number(item?.likes_count ?? item?.reactions_count ?? item?.likes ?? 0);
    const comments = Number(item?.comments_count ?? item?.comment_count ?? item?.comments ?? 0);
    return {
      likes: Number.isFinite(likes) ? likes : 0,
      comments: Number.isFinite(comments) ? comments : 0,
    };
  };

  /** ------------------------ Filtering + SORT DESC ------------------------ **/
  const filteredMedia = useMemo(() => {
    const contributorMatch = (username) => (contributorFilter ? (username || "").toLowerCase().includes(contributorFilter.toLowerCase()) : true);

    let base = [];

    if (filter === "closed") {
      base = finals.filter((f) => contributorMatch(f.user));
    } else if (filter === "all") {
      const medias = mediaList.filter((m) => contributorMatch(m.user));
      const fns = finals.filter((f) => contributorMatch(f.user));
      base = [...fns, ...medias];
    } else {
      base = mediaList.filter((media) => {
        const type = getMediaType(media.file_path || media.location);
        const matchesType = (filter === "videos" && type === "video") || (filter === "images" && type === "image");
        return matchesType && contributorMatch(media.user);
      });
    }

    // ✅ Sort everything by creation date DESC
    return [...base].sort((a, b) => getCreatedTs(b) - getCreatedTs(a));
  }, [filter, contributorFilter, finals, mediaList]);

  /** ------------------------ Infinite scroll sentinel ------------------------ **/
  const sentinelRef = useRef(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        if (isLoading || !hasMore) return;
        if (filter === "closed") return;
        loadPage({ reset: false });
      },
      { rootMargin: "900px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [filter, hasMore, isLoading, loadPage]);

  const filterOptions = [
    { id: "all", label: "Tous", icon: "bi bi-grip-horizontal" },
    { id: "videos", label: "Vidéos", icon: "bi bi-collection-play" },
    { id: "images", label: "Images", icon: "bi bi-image" },
    { id: "closed", label: "Réalisations", icon: "bi bi-patch-check-fill" },
  ];

  /** ------------------------ Styles (Pinterest-like) ------------------------ **/
  const styles = `
  .admin-pin.page-wrap{
    /* ✅ Full width: do NOT reserve any left sidebar width */
    --wv-sidebar-w: 0px;
    --wv-content-pad: 16px;

    padding-top: 10px;
    padding-left: var(--wv-content-pad) !important;
    padding-right: var(--wv-content-pad) !important;

    background: #fafafa;
    min-height: 100vh;
    width: 100%;
    overflow-x: hidden;
  }
  @media (max-width: 720px){
    .admin-pin.page-wrap{
      --wv-content-pad: 12px;
      padding-left: var(--wv-content-pad) !important;
      padding-right: var(--wv-content-pad) !important;
    }
  }

  .admin-pin, .admin-pin *, .admin-pin *::before, .admin-pin *::after{ box-sizing: border-box; }

  .admin-pin .admin-pin-content{ width: 100%; max-width: 100%; }

  /* Top bar */
  .admin-pin .wv-topbar{
    position: fixed;
    top: 70px;
    left: var(--wv-content-pad);
    right: var(--wv-content-pad);
    z-index: 1000;
    background: rgba(250,250,250,.92);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(15, 23, 42, .08);
    padding: 11px 122px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    border-radius: 18px;
  }
  .admin-pin .wv-topbarTitle{
    font-weight: 800;
    color: #0f172a;
    font-size: 14px;
    margin-right: 6px;
  }

/* NEW (match MyMedia chips/search sizing) */
.admin-pin .wv-chip{
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
  font-size: 13px;
  line-height: 1;
  border: 1px solid rgba(15,23,42,.08);
  background: #fff;
  color: #0f172a;
  transition: transform .12s ease, box-shadow .15s ease;
  white-space: nowrap;
}
.admin-pin .wv-chip:hover{ transform: translateY(-1px); box-shadow: 0 10px 20px rgba(15,23,42,.08); }
.admin-pin .wv-chip-active{ background: #111827; color: #fff; border-color: rgba(17,24,39,.45); }

.admin-pin .wv-search{
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  gap: .55rem;
  padding: 8px 12px;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid rgba(15,23,42,.06);
  min-width: 240px;
  max-width: 680px;
  flex: 1;
}
.admin-pin .wv-search i{ color: #6b7280; }
.admin-pin .wv-search input{
  border:none;
  outline:none;
  background:transparent;
  width:100%;
  font-size: 13px;
}



  /* Spacer */
  .admin-pin .filters-spacer{ height: 76px; }

  /* ✅ True masonry (CSS columns) */
  .admin-pin .wv-masonry{
    width: 100%;
    max-width: 100%;
    column-width: clamp(220px, 18vw, 300px);
    column-gap: 14px;
  }

  .admin-pin .wv-masonry-item{
    break-inside: avoid;
    page-break-inside: avoid;
    -webkit-column-break-inside: avoid;
    display: inline-block;
    width: 100%;
    margin: 0 0 14px;
  }

  /* Pin card */
  .admin-pin .media-card{
    position: relative;
    border: none;
    border-radius: 18px;
    background: transparent;
    overflow: visible;
    box-shadow: none;
    transition: transform .16s ease, box-shadow .18s ease;
    will-change: transform;
  }
  .admin-pin .media-card:hover{ transform: translateY(-2px); box-shadow: 0 18px 42px rgba(15,23,42,.14); }

  /* Links */
  .admin-pin .wv-pin-mainLink,
  .admin-pin .wv-pin-metaLink{ display:block; color:inherit; text-decoration:none; }
  .admin-pin .wv-pin-mainLink:focus-visible,
  .admin-pin .wv-pin-metaLink:focus-visible{ outline: 3px solid rgba(17,24,39,.25); outline-offset: 3px; border-radius: 18px; }

  /* ✅ Preview box: FULL media visible (no crop) with ORIGINAL ratio */
   .admin-pin .wv-pin-thumb-wrap{
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    background-color: #0b0f19; /* no white frames */
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  /* ✅ Fake users group (bottom-left on media) */
  .admin-pin .wv-pin-contrib{
    position:absolute;
    left:10px;
    bottom:10px;
    display:flex;
    align-items:center;
    gap:10px;
    z-index: 3;
    pointer-events:none;
  }
  .admin-pin .wv-avatars{ display:flex; align-items:center; }
  .admin-pin .wv-avatarImg{
    width:26px; height:26px;
    border-radius:50%;
    border:2px solid rgba(255,255,255,85);
    box-shadow: 0 10px 22px rgba(15,23,42,.18);
    object-fit:cover;
    margin-left:-8px;
    background: rgba(255,255,255,0.08);
  }
  .admin-pin .wv-avatarImg:first-child{ margin-left:0; }
  .admin-pin .wv-avatarPlus{
    width:26px; height:26px;
    border-radius:50%;
    border:2px solid rgba(255,255,255,85);
    box-shadow: 0 10px 22px rgba(15,23,42,.18);
    margin-left:-8px;
    display:flex; align-items:center; justify-content:center;
    font-size:11px;
    background: rgba(255,255,255,78);
    color:#111;
  }

  .admin-pin .wv-pin-thumb{
    position: relative;
    z-index: 1;
    display:block;
    width: 100%;
    height: 100%;

    /* ✅ NO ZOOM / NO CROP */
    object-fit: contain !important;
    object-position: center !important;

    /* Avoid any inherited transforms */
    transform: none !important;

    background: transparent !important;
  }

  /* ✅ Hard override (in case global CSS touches media tags) */
  .admin-pin .wv-pin-thumb-wrap img,
  .admin-pin .wv-pin-thumb-wrap video{
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
    object-position: center !important;
    background: transparent !important;
    transform: none !important;
  }

  .admin-pin .wv-pin-skeleton{
    display:block;
    width:100%;
    height:100%;
    min-height: 160px;
    animation: wv-shimmer 1.2s infinite linear;
    background: linear-gradient(90deg, #0b0f19, #111827, #0b0f19);
    background-size: 200% 100%;
  }
  @keyframes wv-shimmer{ 0%{ background-position: 200% 0; } 100%{ background-position: -200% 0; } }

  /* Badges */
  .admin-pin .media-visibility,
  .admin-pin .media-kind,
  .admin-pin .media-final{
    position: absolute;
    top: 10px;
    padding: 5px 9px;
    border-radius: 999px;
    background: rgba(17,24,39,.62);
    color: #fff;
    font-size: 11.5px;
    font-weight: 500;
    letter-spacing: .2px;
    backdrop-filter: blur(6px);
    display: inline-flex;
    align-items: center;
    gap: .45rem;
    z-index: 2;
    border: 1px solid rgba(255,255,255,.12);
  }
  .admin-pin .media-visibility{ left: 10px; }
  .admin-pin .media-kind{ right: 10px; }
  .admin-pin .media-final{ left: 50%; transform: translateX(-50%); background: rgba(17,24,39,.78); }


  .admin-pin .wv-pin-creatorAvatar img{ width:100%; height:100%; object-fit: cover !important; display:block; }

  /* Meta */

  .admin-pin .wv-pin-titleLink{
    flex: 1;
    min-width: 0;
  }



/* NEW */
.admin-pin .wv-pin-creatorAvatar{
  width: 28px;
  height: 28px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255,255,255,.95);
  border: 2px solid rgba(255,255,255,.92);
  box-shadow: 0 10px 22px rgba(15,23,42,.18);
  display:flex;
  align-items:center;
  justify-content:center;
  flex: 0 0 auto;
}

.admin-pin .wv-pin-titleRow{
  display:flex;
  align-items:center;
  gap: 8px;
  min-width:0;
  flex-wrap: nowrap;
}
/* NEW CODE (same feel as MyMedia: not bold) */
.admin-pin .wv-pin-title{
  font-weight: 500;   /* ✅ remove bold */
  font-size: 15px;    /* ✅ match MyMedia title size */
  line-height: 1.15;  /* ✅ match MyMedia */
  margin: 0;
  color: #0f172a;
}


.admin-pin .wv-pin-titleLoc{
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, .08);
  background: rgba(248, 250, 252, .9);
  color: rgba(15, 23, 42, .70);
  font-weight: 700;
  font-size: 11px;
  white-space: nowrap;
  max-width: clamp(90px, 28%, 170px);
  overflow: hidden;
  min-width:0;
}

.admin-pin .wv-pin-titleLocText{
  min-width:0;
  overflow:hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

  .admin-pin .wv-pin-statsRight{
    margin-left: auto;               /* push counters to the far right */
    display:flex;
    align-items:center;
    justify-content:flex-end;
    gap:.5rem;
    text-decoration:none;
    color:inherit;
    flex: 0 0 auto;
  }

  .admin-pin .wv-pill-location{
    background:#f3f4f6;
    min-width: 0;
    max-width: clamp(140px, 28vw, 260px);
  }
  .admin-pin .wv-pill-location .wv-pill-text{
    display:inline-block;
    min-width: 0;
    max-width: clamp(90px, 20vw, 200px);
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }


.admin-pin .wv-pin-stats{
  display:flex;
  align-items:center;
  flex-wrap: nowrap;
  gap: 10px;
  margin-top: 6px;
  color: rgba(15, 23, 42, .68);
  font-size: 11px;
  min-width: 0;
}

/* prevent long location from pushing ❤️💬 out */
.admin-pin .wv-pill-location{
  max-width: clamp(120px, 22vw, 220px);
  min-width: 0;
}
.admin-pin .wv-pill-location .wv-pill-text{
  display:inline-block;
  max-width: 100%;
  overflow:hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-pin .wv-pill{
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, .08);
  background: rgba(248, 250, 252, .9);
  font-weight: 700;
  color: rgba(15, 23, 42, .70);
  white-space: nowrap;
  max-width: 100%;
}
.admin-pin .wv-pill-date{ background: rgba(248, 250, 252, .9); }

  /* Footer */
  .admin-pin .card-footer{ background: transparent !important; border:none !important; padding: 2px 6px 10px; color:#6b7280; }
  .admin-pin .wv-footer-inline{ display:flex; flex-wrap: wrap; align-items:center; gap: .55rem .9rem; }

  /* File fallback */
  .admin-pin .wv-pin-fileFallback{
    display:flex;
    align-items:center;
    justify-content:center;
    padding: 28px 10px;
    min-height: 160px;
    border-radius: 18px;
    background: #fff;
    border: 1px dashed rgba(15,23,42,.18);
    color: #6b7280;
    gap: .6rem;
  }
  `;

  const showInitialSpinner = filter !== "closed" && isLoading && mediaList.length === 0 && finals.length === 0;

  return (
    <div className="landing-wrapper">
      <HeaderRunwayStyle />

      <div className="page-wrap admin-pin">
        <style>{styles}</style>



        {/* ✅ Dynamic spacer = topbar height */}
        <div className="filters-spacer" style={{ height: Math.max(76, topbarH + 18) }} />

        {/* Initial loader */}
        {showInitialSpinner ? (
          <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5">
            <div className="spinner-border mb-2" role="status">
              <span className="visually-hidden">Chargement…</span>
            </div>
            <small>Chargement des médias…</small>
          </div>
        ) : (
          <div className="admin-pin-content pb-5">
            {filteredMedia.length ? (
              <div className="wv-masonry">
                {filteredMedia.map((item) => {
                  const isFinal = item.__kind === "project-final";
                  const cardTo = isFinal ? `/mediaDetails/final-${item.projectId}` : `/mediaDetails/${item.id}`;

                  // Source / type
                  let mediaURL = "";
                  let isImage = false;
                  let isVideo = false;
                  let isFile = false;

                  if (isFinal) {
                    mediaURL = item.final_src;
                    isVideo = true;
                  } else {
                    const filePath = item.file_path || item.location || "";
                    const fromUploads = item.file_path !== undefined;
                    mediaURL = `${fromUploads ? "/uploads/" : "/output/"}${filePath}`;

                    const type = getMediaType(filePath);
                    isImage = type === "image";
                    isVideo = type === "video";
                    isFile = type === "file";
                  }

                  const visibility = item?.visibility;

                  // 👤 Creator
                  const creatorUsername = String(resolveProjectCreatorUsername(item) || "").trim();
                  const creatorProfilePath = creatorUsername ? resolveUserProfilePath(creatorUsername) : null;
                  const creatorLabel = creatorUsername ? toCamelCaseName(resolveProjectCreatorLabel(item)) : "";
                  const creatorAvatarSrc = creatorUsername ? avatarFor(creatorUsername, creatorLabel) : "/default-avatar.png";

                  // 📅 Date INLINE
                  const dateLabel = formatDateFr(item?.created_at);

                  return (
                    <div key={item.id} className="wv-masonry-item">
                      <div className="card text-dark media-card">
                        {/* Visual */}
                        <div className="position-relative">
                          <Link to={cardTo} className="wv-pin-mainLink" aria-label={`Ouvrir ${item.title || "média"}`}>
                            {isFile ? (
                              <div className="wv-pin-fileFallback">
                                <i className="bi bi-file-earmark-text" />
                                <span>Fichier</span>
                              </div>
                            ) : (
                              <LazyMediaVisual
                                isImage={isImage}
                                src={mediaURL}
                                title={item.title}
                                isVideo={isFinal || isVideo}
                              />
                            )}
                          </Link>

                          {(() => {
                            const seedKey = isFinal ? `final-${item.projectId}` : (item.id ?? mediaURL ?? "");
                            const users = pickUsers(seedKey);
                            const totalUsers = pickUserCount(seedKey);
                            const extraUsers = Math.max(0, totalUsers - users.length);

                            return (
                              <div className="wv-pin-contrib" aria-hidden="true">
                                <div className="wv-avatars">
                                  {users.map((u) => (
                                    <img
                                      key={u.name}
                                      className="wv-avatarImg"
                                      src={u.img}
                                      alt=""
                                      loading="lazy"
                                    />
                                  ))}
                                  <div className="wv-avatarPlus">+{extraUsers}</div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>


                        {/* Meta */}
                        <div className="card-body d-flex flex-column gap-1">
                          {/* ✅ Avatar BEFORE title + ✅ Location inline on same row */}
                          <div className="wv-pin-titleRow">
                            {creatorUsername ? (
                              creatorProfilePath ? (
                                <Link
                                  to={creatorProfilePath}
                                  className="wv-pin-creatorAvatar"
                                  title={creatorLabel ? `Profil: ${creatorLabel}` : "Profil"}
                                  aria-label={creatorLabel ? `Profil: ${creatorLabel}` : "Profil"}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <img key={avatarBust} src={creatorAvatarSrc} alt={creatorLabel || "Créateur"} loading="lazy" />
                                </Link>
                              ) : (
                                <div className="wv-pin-creatorAvatar" title={creatorLabel || "Créateur"} aria-label={creatorLabel || "Créateur"}>
                                  <img key={avatarBust} src={creatorAvatarSrc} alt={creatorLabel || "Créateur"} loading="lazy" />
                                </div>
                              )
                            ) : null}

                            <Link to={cardTo} className="wv-pin-metaLink wv-pin-titleLink" aria-label="Détails média">
                              <h6 className="wv-pin-title text-truncate" title={item.title}>
                                {item.title}
                              </h6>
                            </Link>

                            {item?.location ? (
                              <span className="wv-pin-titleLoc" title={item.location}>
                                <i className="bi bi-geo-alt" />
                                <span className="wv-pin-titleLocText">{item.location}</span>
                              </span>
                            ) : null}
                          </div>
                        </div>

                        {/* ✅ REMOVE footer block entirely (location moved above) */}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-muted py-5">Aucun média</div>
            )}

            {/* Sentinel + loading hint */}
            <div ref={sentinelRef} />

            <div className="text-center text-muted py-4">
              {filter === "closed" ? null : isLoading ? (
                <span>Chargement des médias…</span>
              ) : hasMore ? (
                <span>Faites défiler pour charger plus</span>
              ) : (
                <span>Fin de la liste</span>
              )}
            </div>
          </div>
        )}
      </div>

      <FooterRunwayStyle />
    </div>
  );

}
